import { LayerCloseHandleV2 } from '@volkswagen-onehub/layer-manager';
import React, { Dispatch, SetStateAction, Suspense, useState } from 'react';
import './VehicleVerification.scss';
import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Infobox,
	Spinner,
	Tabs,
	Text,
	TokenTextAppearance,
	TextTag,
	ThemeProvider,
} from '@volkswagen-onehub/components-core';
import { Checkmark } from '@volkswagen-onehub/icons-core';
import { Modal } from '../../models/Modal';
import { UserAuthenticationData } from '../../models/UserAuthenticationData';
import { authenticationByVehicleCharacteristics } from '../../service/AuthenticationService/AuthenticationService';
import { AuthStatus } from '../../models/AuthStatus';
import { DataRequestState } from '../../models/DataRequestState';
import VehicleVerificationInput from '../../molecules/VehicleVerificationInput/VehicleVerificationInput';
import { PreFilledAuthData } from '../../models/TechnicalData';
import { VehicleObject } from '../../models/Vehicle';

function VehicleVerification({
	state: { setAuthStatus, vin, preFilledAuthData, language, baseApiUrl, baseAssetsUrl, apiKey, trackingService, isLoggedIn, vehicle, t },
	close,
}: Modal<VehicleVerificationProps>) {
	const [userAuthenticationData, setUserAuthenticationData] = useState({} as UserAuthenticationData);
	const [isDataComplete, setIsDataComplete] = useState<boolean>(false);
	const [authButtonDisabled, setAuthButtonDisabled] = useState(false);
	const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
	const [showErrorMessage, setShowErrorMessage] = useState(false);
	const [timeoutInSeconds, setTimeoutInSeconds] = useState(0);

	const timer = (currentSeconds: number) => {
		if (currentSeconds <= 0) {
			setShowErrorMessage(false);
			setAuthButtonDisabled(false);
			setTimeoutInSeconds(0);
			return;
		}

		if (!showErrorMessage) {
			setShowErrorMessage(true);
		}
		setTimeoutInSeconds(currentSeconds);
		setTimeout(() => timer(currentSeconds - 1), 1000);
	};

	const vinCheckTrackingLayerLoad = () => {
		if (trackingService) {
			trackingService.track('VWBasic_Layer_Load', {
				eventInfo: {
					eventAction: 'VWBasic_Layer_Load',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: 'vinDetails.vehicleVerification',
					sectionName: 'vehicleVerification',
					brand: 'passenger',
				},
				user: {
					loginStatus: isLoggedIn,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				configuration: {
					CarlineName: vehicle.data.modelCarLine,
					SalesgroupName: vehicle.data.modelSalesGroup,
				},
			});
		}
	};

	const vinCheckTrackingButton = () => {
		if (trackingService) {
			trackingService.track('VWBasic_Button_Click', {
				eventInfo: {
					eventAction: 'VWBasic_Button_Click',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: 'vinDetails.vehicleVerification',
					sectionName: 'vehicleVerification',
					brand: 'passenger',
				},
				user: {
					loginStatus: isLoggedIn,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				personalization: {
					personalizable: false,
				},
				configuration: {
					CarlineName: vehicle.data.modelCarLine,
					SalesgroupName: vehicle.data.modelSalesGroup,
				},
				link: {
					name: 'authenticationByVehicleCharacteristics',
				},
			});
		}
	};

	vinCheckTrackingLayerLoad();

	return (
		<ThemeProvider theme="main">
			<Container
				wrap="always"
				gutter={ContainerGutter.dynamic0130}
				padding={{ left: ContainerPadding.grid002, right: ContainerPadding.grid002 }}
				shrinkContent
			>
				<Container gutter={ContainerGutter.dynamic0020}>
					<Text appearance={TokenTextAppearance.headline400}>{t('vehicleVerification.title')}</Text>
					<Text tag={TextTag.p}>{t('vehicleVerification.paragraph1')}</Text>
					<Text tag={TextTag.p}>{t('vehicleVerification.paragraph2')}</Text>
				</Container>

				<Container gutter={ContainerGutter.dynamic0050}>
					<div className="green-checkbox">
						<div style={{ zoom: '75%' }}>
							<Checkmark aria-hidden={true} />
						</div>
					</div>
					<Text appearance={TokenTextAppearance.headline200} bold>
						Volkswagen ID
					</Text>
				</Container>

				<Container gutter={ContainerGutter.dynamic0020}>
					<Text appearance={TokenTextAppearance.headline400}>{t('vehicleVerification.secondSection.title')}</Text>
					<Text tag={TextTag.p}>{t('vehicleVerification.secondSection.paragraph1')}</Text>
				</Container>

				<VehicleVerificationInput
					setIsDataComplete={setIsDataComplete}
					userAuthenticationData={userAuthenticationData}
					setUserAuthenticationData={setUserAuthenticationData}
					preFilledAuthData={preFilledAuthData}
				/>

				<Container
					gutter={ContainerGutter.dynamic0050}
					stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b560]: false }}
					wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b560]: 'never' }}
				>
					<CTA tag="button" emphasis="secondary" onClick={close} stretchContent>
						{t('general.cancel')}
					</CTA>

					<CTA
						stretchContent
						tag="button"
						emphasis="primary"
						disabled={!isDataComplete || authButtonDisabled}
						onClick={() => {
							if (!userAuthenticationData.pkn || !userAuthenticationData.variant || !userAuthenticationData.version) {
								return;
							}
							vinCheckTrackingButton();
							setAuthStatus((prevAuthStatus) => ({ ...prevAuthStatus, ...{ state: DataRequestState.Requested } }));
							setAuthButtonDisabled(true);
							setShowLoadingSpinner(true);

							authenticationByVehicleCharacteristics(baseApiUrl, `${apiKey}`, vin, language, userAuthenticationData)
								.then((res) => {
									setAuthStatus((prevAuthStatus) => ({
										data: { loginStatusChanged: true, vinToken: res.token, isAuthenticated: prevAuthStatus.data.isAuthenticated },
										state: DataRequestState.Available,
									}));
									setShowLoadingSpinner(false);
									close();
								})
								.catch(() => {
									timer(10);
									setShowLoadingSpinner(false);
									setAuthStatus((prevAuthStatus) => ({ ...prevAuthStatus, state: DataRequestState.Failed }));
								});
						}}
					>
						{t('vehicleVerification.secondSection.button')}
						{showLoadingSpinner && (
							<div className="identify-button-spinner">
								<Spinner />
							</div>
						)}
					</CTA>
				</Container>

				{showErrorMessage && (
					<div className="auth-error-msg">
						<Infobox aria-live="assertive" appearance={'error'}>
							<Text tag={TextTag.span}>
								{t('vehicleVerification.errorMessage1')}
								{` ${timeoutInSeconds} `}
								{t('vehicleVerification.errorMessage2')}
							</Text>
						</Infobox>
					</div>
				)}

				<div className="verification-layer-third-section">
					<Container gutter={ContainerGutter.dynamic0020} padding={{ bottom: ContainerPadding.dynamic0150 }}>
						<Text appearance={TokenTextAppearance.headline400}>{t('vehicleVerification.thirdSection.title')}</Text>
						<Text tag={TextTag.p}>{t('vehicleVerification.thirdSection.paragraph1')}</Text>
						<Text tag={TextTag.p}>{t('vehicleVerification.thirdSection.paragraph2')}</Text>
					</Container>

					{/* displayed with width above 960px */}
					<Tabs idPrefix="descriptive-image" onChange={() => {}}>
						{{
							title: t('vehicleVerification.thirdSection.productionCode'),
							content: (
								<Container gutter={ContainerGutter.dynamic0040} stretchContent>
									<div
										className="tab-image"
										style={{
											backgroundImage: `url(${baseAssetsUrl}/assets/vehicleAuthentication/pknVariantVersionDoor.jpg)`,
										}}
									/>
									<div
										className="tab-image"
										style={{
											backgroundImage: `url(${baseAssetsUrl}/assets/vehicleAuthentication/PknVariantVersionSketch.png)`,
										}}
									/>
								</Container>
							),
							key: 'Production-code',
						}}
						{{
							title: t('vehicleVerification.thirdSection.registrationDocument'),
							content: (
								<Container stretchContent>
									<div
										className="tab-image"
										style={{
											backgroundImage: `url(${baseAssetsUrl}/assets/vehicleAuthentication/pknVariantVersionCoC.png)`,
											width: 'calc(50% - var(--size-dynamic0040) / 2)',
										}}
									/>
								</Container>
							),
							key: 'Registration-document',
						}}
					</Tabs>

					{/* displayed with width below 960px */}
					<div className="fanned-images">
						<Text appearance={TokenTextAppearance.headline100} bold>
							{t('vehicleVerification.thirdSection.productionCode')}
						</Text>
						<div
							className="tab-image"
							style={{ backgroundImage: `url(${baseAssetsUrl}/assets/vehicleAuthentication/pknVariantVersionDoor.jpg)` }}
						/>
						<div
							className="tab-image second-image"
							style={{ backgroundImage: `url(${baseAssetsUrl}/assets/vehicleAuthentication/PknVariantVersionSketch.png)` }}
						/>
						<Text appearance={TokenTextAppearance.headline100} bold>
							{t('vehicleVerification.thirdSection.registrationDocument')}
						</Text>
						<div className="tab-image" style={{ backgroundImage: `url(${baseAssetsUrl}/assets/vehicleAuthentication/pknVariantVersionCoC.png)` }} />
						<div className="end-of-page">-</div>
					</div>
				</div>
			</Container>
		</ThemeProvider>
	);
}

export default function renderVehicleVerification(state: VehicleVerificationProps, close: LayerCloseHandleV2<any>): JSX.Element {
	return (
		<Suspense fallback>
			<VehicleVerification state={state} close={close} />{' '}
		</Suspense>
	);
}

interface VehicleVerificationProps {
	authStatus: AuthStatus;
	setAuthStatus: Dispatch<SetStateAction<AuthStatus>>;
	vin: string;
	preFilledAuthData: PreFilledAuthData;
	language: string;
	baseApiUrl: string;
	baseAssetsUrl: string;
	apiKey: string;
	trackingService: any;
	isLoggedIn: boolean;
	vehicle: VehicleObject;
	tracking: {
		contentId: string;
		sectionName: string;
	};
	t: any;
}
